import { css } from '@emotion/react';

import { classes as buttonBaseClasses } from '@core/ButtonBase/getButtonBaseCss';
import { classes as inputClasses } from '@core/forms/Input/getInputCss';
import generateEmotionClasses from '@core/utils/generateEmotionClasses';

export const classes = generateEmotionClasses('selectController', [
  'root',
  'active',
  'label',
  'value',
  'icon',
  'itemsNumber',
  'outlined',
  'silent',
  'fullWidth',
  'readOnly',
]);

export type VisualProps = {
  width?: number;
};

const setFixedWidth = (width: VisualProps['width']) =>
  width
    ? css`
        max-width: unset;
        width: ${width}px;
      `
    : undefined;

const styles = (props: VisualProps) => css`
  max-width: 400px;
  padding: var(--cds-spacing-150);
  color: var(--cds-color-neutral-primary-weak);
  justify-content: flex-start;
  gap: var(--cds-spacing-50);

  ${setFixedWidth(props.width)}

  .${classes.label} {
    text-align: left;
  }

  .${classes.value} {
    text-align: left;
    color: var(--cds-color-neutral-primary);
  }

  .${classes.itemsNumber} {
    min-width: 32px;
    margin-right: var(--cds-spacing-100);
  }

  .${classes.icon} {
    display: inline-flex;
    margin-right: 0;
    margin-left: auto;
  }

  &:hover,
  &.${buttonBaseClasses.focusVisible}, &.${classes.active} {
    color: var(--cds-color-neutral-primary);
  }

  &.${classes.outlined} {
    box-shadow: inset 0 0 0 1px var(--cds-color-neutral-stroke-primary);

    &:hover,
    &.${buttonBaseClasses.focusVisible} {
      background: var(--cds-color-interactive-background-primary-hover-weak);
      box-shadow: inset 0 0 0 1px var(--cds-color-interactive-primary-hover);
    }

    &.${classes.active} {
      box-shadow: inset 0 0 0 1px var(--cds-color-interactive-primary-hover);
    }
  }

  &.${classes.silent} {
    &::before {
      content: '';
      position: absolute;
      bottom: 0;
      left: 0;
      right: 0;
    }

    &:hover:not(.${buttonBaseClasses.focusVisible}) {
      background: var(--cds-color-interactive-background-primary-hover-weak);

      &::before {
        border-bottom: 2px solid var(--cds-color-interactive-primary-hover);
      }
    }

    &.${classes.active} {
      &::before {
        border-bottom: 2px solid var(--cds-color-interactive-primary-hover);
      }
    }
  }

  &.${classes.fullWidth} {
    max-width: none;
    width: 100%;
  }

  &.${classes.readOnly} {
    cursor: not-allowed;
    background: var(--cds-color-neutral-disabled-weak);
    box-shadow: inset 0 0 0 1px var(--cds-color-neutral-disabled-strong);

    &:hover {
      background: var(--cds-color-neutral-disabled-weak);
      box-shadow: inset 0 0 0 1px var(--cds-color-neutral-stroke-primary);
    }
  }

  /* Validation styles */

  .${inputClasses.invalid} &.${classes.outlined} {
    box-shadow: inset 0 0 0 1px var(--cds-color-feedback-error);

    &:hover,
    &.${buttonBaseClasses.focusVisible} {
      background: var(--cds-color-feedback-background-error-weak);
      box-shadow: inset 0 0 0 1px var(--cds-color-feedback-error-hover);
      color: var(--cds-color-neutral-primary);
    }

    &.${buttonBaseClasses.focusVisible} {
      background: none;
    }
  }

  .${inputClasses.valid} &.${classes.outlined} {
    box-shadow: inset 0 0 0 1px var(--cds-color-feedback-success);

    &:hover,
    &.${buttonBaseClasses.focusVisible} {
      background: var(--cds-color-feedback-background-success-weak);
      box-shadow: inset 0 0 0 1px var(--cds-color-feedback-success-hover);
      color: var(--cds-color-neutral-primary);
    }

    &.${buttonBaseClasses.focusVisible} {
      background: none;
    }
  }
`;

export default styles;
